import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAdminApps, fetchRoleKeys, fetchRoleMapCoverage, fetchRoleMaps } from '@/api/console';

/**
 * EAA-1817: the four queries every part of `/console/role-coverage` reads
 * from, plus the capability flags that gate them.
 *
 * EAA-1817: this board is DELIBERATELY not org-scoped — `fetchRoleMapCoverage`
 * (`RoleMapService.coverage()`) filters `organization__isnull=True`
 * server-side and takes no organization param at all, so it only ever
 * reflects ECOSYSTEM-DEFAULT links. `fetchRoleMaps()` below is filtered the
 * same way client-side (`organization_id === null`) so the editor's chip ids
 * agree with what the matrix shows. Do not add an acting-org scope switch to
 * this screen — there is nothing for it to switch between.
 *
 * GATE TRAP (see `api/console.ts` docstrings): `GET /admin/app-roles` is
 * `IsSuperAdmin`-only regardless of params, and `console.rolemap.manage` is
 * the capability that already gates every default-scope RoleMap write
 * elsewhere in this console (`roleMap/useRoleMapTable.ts`). Since a caller
 * who may write the default scope IS the same super-admin population that
 * endpoint requires, `canManage` doubles as the gate for the "+ vai trò"
 * picker and the "Xem danh mục" detail view — never call `fetchAppRoles` for
 * a caller without it.
 */
export function useRoleCoverageData(active = true) {
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();

    const canView = can('console.rolemap.view');
    const canManage = can('console.rolemap.manage');
    const canSync = can('console.rolemap.sync');
    // EAA-1841: false while the merged screen is showing the list view — see
    // `useRoleMapTable`'s `active` note for why gating queries (not the hook
    // call) is what stops the hidden view from fetching.
    const enabled = active && authenticated && Boolean(accessToken) && canView;
    const apiBase = config.apiBase;
    const token = accessToken;

    const coverageQuery = useQuery({
        queryKey: ['role-map-coverage', accessToken],
        queryFn: () => fetchRoleMapCoverage(apiBase, token as string),
        enabled,
        retry: false,
    });

    const roleKeysQuery = useQuery({
        queryKey: ['role-keys', accessToken],
        queryFn: () => fetchRoleKeys(apiBase, token as string),
        enabled,
        retry: false,
    });

    const adminAppsQuery = useQuery({
        queryKey: ['admin-apps', accessToken],
        queryFn: () => fetchAdminApps(apiBase, token as string),
        enabled,
        retry: false,
    });

    // EAA-1817: the flat rows back the editor card only (chip ids, staleness,
    // per-app link counts) — the matrix itself reads `coverageQuery` alone.
    const roleMapsQuery = useQuery({
        queryKey: ['role-maps', accessToken],
        queryFn: () => fetchRoleMaps(apiBase, token as string),
        enabled,
        retry: false,
    });

    return {
        apiBase,
        token,
        canView,
        canManage,
        canSync,
        enabled,
        coverageQuery,
        roleKeysQuery,
        adminAppsQuery,
        roleMapsQuery,
    };
}

export type RoleCoverageData = ReturnType<typeof useRoleCoverageData>;
